const FADE_MS = 300;
// gears-api shows the last frame 2760 ms in (400 ms bare map + 59 × 40 ms painting); the
// finished track then stays a moment before fading back.
const PLAY_MS = 3400;
// Mirrors the gears-api cap for `?animated`.
const MAX_ANIMATED_PIXELS = 640 * 480;
const MAX_ANIMATED_SIDE = 1024;

export function canAnimatePreview(width: number, height: number): boolean {
  return width * height <= MAX_ANIMATED_PIXELS && Math.max(width, height) <= MAX_ANIMATED_SIDE;
}

function prefersAnimatedPreview(): boolean {
  return (
    window.matchMedia?.('(hover: hover) and (prefers-reduced-motion: no-preference)').matches ??
    false
  );
}

/**
 * When the pointer enters `target`, fades `overlay` in with the animated preview, plays it once
 * and fades back to the static image. Leaving fades back early. Returns a cleanup function.
 */
export function attachPreviewHover(
  target: HTMLElement,
  overlay: HTMLImageElement,
  animatedUrl: string,
): () => void {
  if (!prefersAnimatedPreview()) return () => {};

  let blob: Promise<Blob> | null = null;
  let objectUrl: string | null = null;
  let hover = 0;
  let visible = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  overlay.style.opacity = '0';
  overlay.style.transition = `opacity ${FADE_MS}ms ease-in-out`;
  overlay.style.pointerEvents = 'none';

  const clear = () => {
    overlay.removeAttribute('src');
    if (objectUrl != null) URL.revokeObjectURL(objectUrl);
    objectUrl = null;
  };

  const hide = () => {
    visible = false;
    clearTimeout(timer);
    if (overlay.style.opacity === '1') overlay.style.opacity = '0';
    else clear();
  };

  const onEnter = async () => {
    visible = true;
    const current = ++hover;
    blob ??= fetch(animatedUrl).then((res) => {
      if (!res.ok) throw new Error(`Animated preview failed: ${res.status}`);
      return res.blob();
    });

    try {
      const data = await blob;
      if (current !== hover) return;
      clearTimeout(timer);
      clear();
      // A fresh object URL is a new image resource, so the animation restarts at frame 0.
      objectUrl = URL.createObjectURL(data);
      overlay.src = objectUrl;
      await overlay.decode();
    } catch {
      blob = null;
      return;
    }
    if (current !== hover || !visible) return;
    overlay.style.opacity = '1';
    timer = setTimeout(hide, PLAY_MS);
  };

  const onLeave = () => {
    hover++;
    hide();
  };

  const onTransitionEnd = () => {
    if (!visible && overlay.style.opacity === '0') clear();
  };

  target.addEventListener('pointerenter', onEnter);
  target.addEventListener('pointerleave', onLeave);
  overlay.addEventListener('transitionend', onTransitionEnd);

  return () => {
    target.removeEventListener('pointerenter', onEnter);
    target.removeEventListener('pointerleave', onLeave);
    overlay.removeEventListener('transitionend', onTransitionEnd);
    clearTimeout(timer);
    clear();
  };
}
